import axios from 'axios';
import { UPDATE_JOB_PROFILE_DETAILS_URL } from '@/utils/constants/constants.js';

function get_nested_subsection(sections, sidx, subIdx) {
  const section = Reflect.get(sections, sidx);
  const subsections = Reflect.get(section || {}, 'subsections');
  return Array.isArray(subsections) ? Reflect.get(subsections, subIdx) : null;
}

function set_description_value(target, dIdx, value) {
  const descriptions = Reflect.get(target || {}, 'descriptions');
  if (Array.isArray(descriptions) && Reflect.get(descriptions, dIdx) !== undefined) Reflect.set(descriptions, dIdx, value);
}

function set_table_cell_value(target, rIdx, cIdx, value) {
  const row = get_table_row(target, rIdx);
  if (can_set_table_cell(row, cIdx)) Reflect.set(row, cIdx, value);
}
function get_table_row(target, rIdx) {
  const table = Reflect.get(target || {}, 'table');
  const rows = Reflect.get(table || {}, 'rows');
  return Array.isArray(rows) ? Reflect.get(rows, rIdx) : null;
}
function can_set_table_cell(row, cIdx) {
  return Array.isArray(row) && Reflect.get(row, cIdx) !== undefined;
}

function is_successful_save_response(response) {
  return response.data?.status === 'success' || response.data?.data;
}

const methods = {
    async submit_form() {
      this.sync_editors_to_sections();
      const payload = this.build_job_profile_save_payload(this.sections);
      this.save_state = 'saving';
      this.save_message = 'Saving changes...';
      try {
        await this.upload_replacement_job_profile_file();
        const response = await axios.post(UPDATE_JOB_PROFILE_DETAILS_URL, payload, {
          headers: { ...this.headers, 'Content-Type': 'application/json' }
        });
        await this.handle_job_profile_save_response(response);
      } catch (error) {
        this.handle_job_profile_save_error(error);
      }
    },

    sync_editors_to_sections() {
      Object.entries(this._editorInstances || {}).forEach(([editor_id, editor]) => {
        if (editor) this.sync_editor_to_section(editor_id, editor.getData());
      });
    },

    sync_editor_to_section(editor_id, value) {
      return this.sync_description_editor(editor_id, value)
        || this.sync_sub_description_editor(editor_id, value)
        || this.sync_table_cell_editor(editor_id, value)
        || this.sync_sub_table_cell_editor(editor_id, value);
    },

    sync_description_editor(editor_id, value) {
      const match = editor_id.match(/description_text_(\d+)_(\d+)/);
      if (!match) return false;
      const [, sidx, dIdx] = match.map(Number);
      set_description_value(Reflect.get(this.sections, sidx), dIdx, value);
      return true;
    },

    sync_sub_description_editor(editor_id, value) {
      const match = editor_id.match(/sub_description_text_(\d+)_(\d+)_(\d+)/);
      if (!match) return false;
      const [, sidx, subIdx, dIdx] = match.map(Number);
      set_description_value(get_nested_subsection(this.sections, sidx, subIdx), dIdx, value);
      return true;
    },

    sync_table_cell_editor(editor_id, value) {
      const match = editor_id.match(/table_cell_(\d+)_(\d+)_(\d+)/);
      if (!match) return false;
      const [, sidx, rIdx, cIdx] = match.map(Number);
      set_table_cell_value(Reflect.get(this.sections, sidx), rIdx, cIdx, value);
      return true;
    },

    sync_sub_table_cell_editor(editor_id, value) {
      const match = editor_id.match(/sub_table_cell_(\d+)_(\d+)_(\d+)_(\d+)/);
      if (!match) return false;
      const [, sidx, subIdx, rIdx, cIdx] = match.map(Number);
      set_table_cell_value(get_nested_subsection(this.sections, sidx, subIdx), rIdx, cIdx, value);
      return true;
    },

    build_job_profile_save_payload(source_sections) {
      const document_payload = this.build_job_profile_document_payload(source_sections);
      const payload = {
        entity_type_id: this.jobId,
        document: document_payload
      };
      return payload;
    },
    build_job_profile_document_payload(source_sections) {
      const document_payload = {
        id: this.form_detail.id,
        title: this.form_detail.title,
        description: this.form_detail.description,
        sections: this.build_sections_payload(source_sections)
      };
      return document_payload;
    },

    async handle_job_profile_save_response(response) {
      if (!is_successful_save_response(response)) throw new Error('Invalid response');
      this.save_state = 'saved';
      this.save_message = 'Changes saved successfully';
      this.show_success('Job profile updated successfully');
      await this.load_job_profile();
      this.finish_job_profile_save_success();
    },

    finish_job_profile_save_success() {
      if (this.embeddedEditModal) this.$emit('profile-saved', { jobId: this.jobId, title: this.form_detail.title });
      else this.mode = 'view';
      setTimeout(() => {
        this.save_state = 'idle';
        this.save_message = '';
      }, 3000);
    },

    handle_job_profile_save_error(error) {
      this.save_state = 'error';
      this.save_message = 'Failed to save changes';
      this.show_error(`Failed to save: ${error.response?.data?.message || error.message}`);
      setTimeout(() => {
        this.save_state = 'idle';
        this.save_message = '';
      }, 5000);
    },

    async save_show_on_advert() {
      const source_sections = this.get_advert_visibility_source_sections();
      if (!source_sections?.length) return;
      const payload = this.build_job_profile_save_payload(source_sections);
      try {
        await axios.post(UPDATE_JOB_PROFILE_DETAILS_URL, payload, {
          headers: { ...this.headers, 'Content-Type': 'application/json' },
        });
        this.show_success('Advert visibility updated');
        this.sync_advert_visibility_sections();
      } catch (error) {
        this.show_error(`Failed to update: ${error.response?.data?.message || error.message}`);
      }
    },
    get_advert_visibility_source_sections() {
      return this.mode === 'edit' ? this.sections : this.form_detail.sections;
    },

    sync_advert_visibility_sections() {
      if (this.mode === 'edit') this.form_detail.sections = this.clone_sections_for_edit(this.sections);
      else this.sections = this.clone_sections_for_edit(this.form_detail.sections);
    },

    export_html() {
      this.show_info('HTML export feature coming soon');
    },

    export_pdf() {
      this.show_info('PDF export feature coming soon');
    }
};

export default methods;
